import React, { useEffect, useState } from 'react';

import {
  AdmissionTeamMemberCard,
  AdmissionsTeamMemberCardProps,
  ChakraHStack,
  ChakraVStack,
  FAIconName,
  FireflyButton,
  ModalV2,
  ProviderAvatarBlockCardProps,
} from '@equip.health/ui';
import ProviderUniversalSelect from '~/components/common/calendar/ProviderUniversalSelect';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';
const TOP_LEVEL_ID = 'edit-admissions-team-modal';

interface EditAdmissionsTeamModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  admissionsTeamMembers: ProviderAvatarBlockForState[];
  initialAdmissionsTeamMembers:
    | AdmissionsTeamMemberCardProps[]
    | ProviderAvatarBlockCardProps[];
  isUpdatingAssignments: boolean;
  setAdmissionsTeamMembers: React.Dispatch<
    React.SetStateAction<ProviderAvatarBlockForState[]>
  >;
}

const modalArgs = {
  bodyStyle: {
    overflow: 'visible',
  },
  footerStyle: {
    padding: '0px',
  },
  title: 'Edit admissions team',
  titleStyle: {
    flex: '1 0 0',
    padding: '0px',
  },
};

const EditAdmissionsTeamModal = ({
  isOpen,
  onCancel,
  onConfirm,
  admissionsTeamMembers,
  initialAdmissionsTeamMembers,
  isUpdatingAssignments,
  setAdmissionsTeamMembers,
}: EditAdmissionsTeamModalProps) => {
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [isInitialAdmissionsTeamAdded, setIsInitialAdmissionsTeamAdded] =
    useState<boolean>(false);

  useEffect(() => {
    if (!isInitialAdmissionsTeamAdded) {
      setAdmissionsTeamMembers(initialAdmissionsTeamMembers);
      setIsInitialAdmissionsTeamAdded(true);
    }
  }, []);
  const handleAddAdmissionsTeam = (value: ProviderUniversalSelectOption) => {
    const firstOutOfOffice = value.outOfOffice || [];
    const item: ProviderAvatarBlockForState = {
      avatarUrl: value.avatar?.thumbnailUrl,
      legalName: {
        firstName: value.firstName,
        lastName: value.lastName,
      },
      displayName: {
        firstName: value.displayFirstName,
        lastName: value.displayLastName,
      },
      oooDate: getLatestOutOfOfficeEndDate(firstOutOfOffice),
      id: value.userId,
      providerTypeName: value.providerTypeDetails?.name,
    };
    const newArray = [...admissionsTeamMembers];
    if (newArray.findIndex((i) => i.id === item.id) === -1) {
      newArray.push(item);
      setIsChanged(true);
      setAdmissionsTeamMembers(newArray);
    }
  };

  const removeAdmissionsTeamMember = (userId: string) => {
    const newArr = admissionsTeamMembers.filter((i) => i.id !== userId);
    if (newArr.length < admissionsTeamMembers.length) {
      setAdmissionsTeamMembers(newArr);
      setIsChanged(true);
    }
  };
  return (
    <ModalV2
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            leftIcon={FAIconName.timesRegular}
            onClick={onCancel}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__confirm`}
            isDisabled={!isChanged || isUpdatingAssignments}
            leftIcon={FAIconName.checkRegular}
            onClick={onConfirm}
          >
            Save changes
          </FireflyButton>
        </ChakraHStack>
      }
      {...modalArgs}
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={onCancel}
      size="lg"
    >
      <ChakraVStack gap="24px" width="100%">
        <div style={{ width: '100%' }}>
          <ProviderUniversalSelect
            customPlaceholder="Search by name or role to add team members"
            handleSelect={handleAddAdmissionsTeam}
            id={TOP_LEVEL_ID}
            modalId={`${TOP_LEVEL_ID}__universal-select-modal`}
            selectedProvider={null}
          />
        </div>
        <ChakraVStack gap="12px" maxHeight="384px" overflow="auto" width="100%">
          {admissionsTeamMembers.map((o) => {
            return (
              <AdmissionTeamMemberCard
                avatarUrl={o.avatarUrl}
                firstName={o.displayName?.firstName || o.legalName?.firstName}
                id={`${TOP_LEVEL_ID}__admissions-team-member-card__${o.id}`}
                key={o.id}
                lastName={o.displayName?.lastName || o.legalName?.lastName}
                onCloseButtonClick={() => removeAdmissionsTeamMember(o.id)}
                role={o.providerTypeName}
                oooDate={o.oooDate}
              />
            );
          })}
        </ChakraVStack>
      </ChakraVStack>
    </ModalV2>
  );
};

export default EditAdmissionsTeamModal;
